import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import AdminIcon from './AdminIcon';

export default function ConfirmDialog({ title, children, busy, error, danger, confirmLabel, onCancel, onConfirm }: {
  title: string; children: ReactNode; busy: boolean; error: string | null; danger?: boolean;
  confirmLabel: string; onCancel: () => void; onConfirm: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const busyRef = useRef(busy);
  const onCancelRef = useRef(onCancel);
  busyRef.current = busy;
  onCancelRef.current = onCancel;

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    cancelRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const keydown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && !busyRef.current) { event.preventDefault(); onCancelRef.current(); }
      if (event.key !== 'Tab') return;
      const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], [tabindex="0"]') ?? []);
      if (!items.length) { event.preventDefault(); panelRef.current?.focus(); return; }
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement as HTMLElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement as HTMLElement))) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', keydown);
      if (previous?.isConnected) previous.focus();
    };
  }, []);

  return createPortal(<div className="admin-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
    <div ref={panelRef} className="admin-dialog" role="dialog" aria-modal="true" aria-labelledby="admin-dialog-title" aria-describedby="admin-dialog-description" aria-busy={busy} tabIndex={-1}>
      <div className="admin-dialog-heading"><h2 id="admin-dialog-title">{title}</h2><button type="button" className="admin-dialog-close" onClick={onCancel} disabled={busy} aria-label="关闭"><AdminIcon name="close" /></button></div>
      <div id="admin-dialog-description" className="admin-dialog-description">{children}</div>
      {error && <p className="admin-dialog-error" role="alert">{error}</p>}
      <div className="admin-dialog-actions"><button ref={cancelRef} type="button" className="admin-button" onClick={onCancel} disabled={busy}>取消</button><button type="button" className={`admin-button ${danger ? 'admin-button-danger' : 'admin-button-primary'}`} onClick={onConfirm} disabled={busy}>{busy ? '处理中…' : confirmLabel}</button></div>
    </div>
  </div>, document.body);
}
